import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Eye, EyeOff, RefreshCw, X, Loader2 } from "lucide-react";
import { endpoints } from "@/config/endpoints";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { authService } from "@/services/auth.service";

interface Product {
  _id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddSupplier: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = React.useState({
    name: "",
    email: "",
    phone: "",
    roles: ["supplier"],
    category: "",
    location: "",
    products: [] as string[],
  });

  const [open, setOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    data: products = [],
    isLoading: isLoadingProducts,
    error: productsError,
  } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      try {
        const { token } = authService.getStoredAuth();
        if (!token) {
          throw new Error("No authentication token found");
        }

        const response = await fetch(endpoints.products.list, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            try {
              const newToken = await authService.refreshToken();
              const retryResponse = await fetch(endpoints.products.list, {
                headers: {
                  Authorization: `Bearer ${newToken}`,
                },
              });
              if (!retryResponse.ok) {
                throw new Error("Failed to fetch products after token refresh");
              }
              const data = await retryResponse.json();
              return Array.isArray(data) ? data : [];
            } catch (error) {
              console.error("Error refreshing token:", error);
              return [];
            }
          }
          throw new Error("Failed to fetch products");
        }

        const data = await response.json();
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error fetching products:", error);
        return [];
      }
    },
    initialData: [],
  });

  const [searchQuery, setSearchQuery] = useState("");
  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return products.filter((product) =>
      product.title.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [products, searchQuery]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      roles: [value],
    }));
  };

  const handleCategoryChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      category: value,
    }));
  };

  const handleProductSelect = (productId: string) => {
    setFormData((prev) => {
      const currentProducts = Array.isArray(prev.products) ? prev.products : [];
      const products = currentProducts.includes(productId)
        ? currentProducts.filter((id) => id !== productId)
        : [...currentProducts, productId];
      return { ...prev, products };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Format products data to match the server schema
      const formattedProducts = formData.products.map((productId) => {
        const product = products.find((p) => p._id === productId);
        return {
          name: product?.title || "",
          description: product?.description || "",
          unit: "",
          price: 0,
        };
      });

      const supplierData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        category: formData.category,
        location: formData.location,
        products: formattedProducts,
        password: "defaultPassword123", // Temporary password for supplier
      };

      const response = await fetch(`${endpoints.suppliers.create}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(supplierData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Failed to register supplier"
        );
      }

      toast.success("Supplier registered successfully");
      onClose();
      onSuccess?.();
      setFormData({
        name: "",
        email: "",
        phone: "",
        roles: ["supplier"],
        category: "",
        location: "",
        products: [],
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to register supplier";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader className="sticky top-0 bg-background z-10 mt-1 flex gap-2">
          <DialogTitle className="text-primary">Add New Supplier</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="roles">Role</Label>
            <Select onValueChange={handleRoleChange} value="supplier" disabled>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="supplier">Supplier</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="category">Category</Label>
            <Select onValueChange={handleCategoryChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="company">Company</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="name">
              {formData.category === "individual"
                ? "Individual Name"
                : formData.category === "company"
                ? "Company Name"
                : "Name"}
            </Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="email">Email </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="space-y-4">
            <Label>Products</Label>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-full justify-between"
                >
                  {formData.products?.length > 0
                    ? `${formData.products.length} products selected`
                    : "Select products..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                {isLoadingProducts ? (
                  <div className="flex items-center justify-center p-4">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="ml-2">Loading products...</span>
                  </div>
                ) : productsError ? (
                  <div className="text-red-500 p-4">Error loading products</div>
                ) : (
                  <div className="p-2">
                    <Input
                      placeholder="Search products..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="mb-2"
                    />
                    <div className="max-h-[200px] overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <div className="text-sm text-muted-foreground p-2">
                          No products found.
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {filteredProducts.map((product) => (
                            <div
                              key={product._id}
                              className="flex items-center justify-between p-2 hover:bg-accent rounded-md cursor-pointer"
                              onClick={() => handleProductSelect(product._id)}
                            >
                              <span className="text-sm">{product.title}</span>
                              <Check
                                className={cn(
                                  "h-4 w-4",
                                  formData.products?.includes(product._id)
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {formData.products && formData.products.length > 0 && (
              <div className="mt-2">
                <div className="flex flex-wrap gap-2">
                  {formData.products.map((productId) => {
                    const product = products?.find((p) => p._id === productId);
                    return product ? (
                      <div
                        key={productId}
                        className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded"
                      >
                        <span className="text-sm">{product.title}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                          onClick={() => handleProductSelect(productId)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                "Add Supplier"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSupplier;
